import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Download, Send, UserPlus2 } from "lucide-react";
import { toast } from "sonner";
import { userApi, apiError } from "@/lib/userApi";
import { Avatar } from "@/components/community/ChatList";
import { Comments } from "@/components/feed/Comments";
import { RichText } from "@/components/feed/MentionInput";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const src = (m) => `${BACKEND}${m.url}`;

/** Visor a pantalla completa: galería con flechas, deslizar, miniaturas,
 *  descarga, envío a Telegram, reacciones y comentarios. */
export const MediaLightbox = ({
  list, index, onIndex, onClose, post, me, reactions = [], onReact, onCommentAdded,
}) => {
  const total = list.length;
  const item = list[index];
  const touch = useRef(null);
  const [sending, setSending] = useState(false);
  const [friends, setFriends] = useState(null);   // null = menú cerrado

  const move = useCallback((step) => {
    onIndex((index + step + total) % total);
  }, [index, total, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && total > 1) move(1);
      else if (e.key === "ArrowLeft" && total > 1) move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, onClose, total]);

  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onTouchEnd = (e) => {
    if (!touch.current || total < 2) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) move(dx < 0 ? 1 : -1);
  };

  const sendToTelegram = async () => {
    setSending(true);
    try {
      await userApi.post(`/feed/${post.post_id}/send-telegram`, { media_id: item.media_id });
      toast.success("Te la mandé por Telegram");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const openFriends = async () => {
    if (friends) return setFriends(null);
    try {
      const { data } = await userApi.get("/social/friends");
      setFriends(data.friends || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  const shareWith = async (person) => {
    setSending(true);
    try {
      const { data: conv } = await userApi.post("/community/conversations",
        { type: "dm", member: person.key });
      await userApi.post(`/community/conversations/${conv.conv_id}/messages`,
        { media_id: item.media_id, kind: item.mime?.startsWith("video/") ? "video" : "image",
          text: post?.text ? `📷 ${post.text.slice(0, 120)}` : "" });
      toast.success(`Se la mandé a ${person.name}`);
      setFriends(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (!item) return null;
  const commentsOff = post?.comments_mode === "nobody";

  // Portal: las tarjetas usan backdrop-filter y eso atraparía un `position: fixed`.
  return createPortal((
    <div data-testid="media-lightbox"
      className="fixed inset-0 z-50 flex flex-col lg:flex-row theme-noir"
      style={{ background: "rgba(3,3,5,0.94)", backdropFilter: "blur(8px)" }}>

      <div className="relative flex-1 flex flex-col min-h-0"
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>

        {total > 1 && (
          <div data-testid="lightbox-strip"
            className="shrink-0 flex items-center justify-center gap-1 pt-3 px-3 pointer-events-none">
            {list.slice(0, 14).map((m, i) => (
              <span key={m.media_id}
                className={`block rounded-[3px] overflow-hidden border transition-opacity ${
                  i === index ? "border-[#FF8A2B] opacity-100" : "border-white/15 opacity-45"}`}
                style={{ width: 22, height: 22 }}>
                <img src={src(m)} alt="" className="w-full h-full object-cover" />
              </span>
            ))}
          </div>
        )}

        <div className="relative flex-1 flex items-center justify-center p-3 min-h-0"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
          {item.mime?.startsWith("video/") ? (
            <video src={src(item)} controls autoPlay
              className="max-w-full max-h-full rounded-lg" data-testid="lightbox-video" />
          ) : (
            <img src={src(item)} alt={item.name} data-testid="lightbox-image"
              className="max-w-full max-h-full object-contain rounded-lg" />
          )}

          {total > 1 && (
            <>
              <button data-testid="lightbox-prev" onClick={() => move(-1)} aria-label="Anterior"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/85 hover:bg-white/10"
                style={{ background: "rgba(12,12,16,0.7)" }}>
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button data-testid="lightbox-next" onClick={() => move(1)} aria-label="Siguiente"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/85 hover:bg-white/10"
                style={{ background: "rgba(12,12,16,0.7)" }}>
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          <div className="absolute top-3 right-3 flex items-center gap-2">
            {post && (
              <div className="relative">
                <button data-testid="lightbox-share-friend" onClick={openFriends} disabled={sending}
                  title="Mandarla a un amigo"
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/85 hover:bg-white/10 disabled:opacity-50"
                  style={{ background: "rgba(12,12,16,0.7)" }}>
                  <UserPlus2 className="w-4 h-4" />
                </button>
                {friends && (
                  <div data-testid="lightbox-friend-list"
                    className="absolute right-0 top-12 w-60 max-h-72 overflow-y-auto p-2 rounded-2xl border border-white/10 z-20"
                    style={{ background: "rgba(10,10,14,0.96)", backdropFilter: "blur(18px)" }}>
                    {friends.length === 0 ? (
                      <div className="text-[11px] text-white/50 px-2 py-3">
                        Todavía no tienes amigos para compartirle.
                      </div>
                    ) : friends.map((p) => (
                      <button key={p.key} data-testid={`share-to-${p.key}`} disabled={sending}
                        onClick={() => shareWith(p)}
                        className="w-full flex items-center gap-2 px-2 py-2 rounded-xl text-left hover:bg-white/8 disabled:opacity-50">
                        <Avatar person={p} size={28} />
                        <span className="text-xs text-white truncate">{p.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {post && (
              <button data-testid="lightbox-telegram" onClick={sendToTelegram} disabled={sending}
                title="Mandármela por Telegram"
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/85 hover:bg-white/10 disabled:opacity-50"
                style={{ background: "rgba(12,12,16,0.7)" }}>
                <Send className="w-4 h-4" />
              </button>
            )}
            <a data-testid="lightbox-download" href={src(item)} download={item.name || "imagen"}
              title="Guardar la imagen original"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/85 hover:bg-white/10"
              style={{ background: "rgba(12,12,16,0.7)" }}>
              <Download className="w-4 h-4" />
            </a>
            <button data-testid="lightbox-close" onClick={onClose} aria-label="Cerrar"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/85 hover:bg-white/10"
              style={{ background: "rgba(12,12,16,0.7)" }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {total > 1 && (
          <div data-testid="lightbox-thumbs"
            className="shrink-0 flex items-center justify-center gap-2 px-3 pb-4 pt-1 overflow-x-auto">
            <span data-testid="lightbox-counter"
              className="text-[11px] text-white/55 mr-1 shrink-0">{index + 1} / {total}</span>
            {list.map((m, i) => (
              <button key={m.media_id} data-testid={`lightbox-thumb-${i}`} onClick={() => onIndex(i)}
                className={`shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                  i === index ? "border-[#FF8A2B] scale-105" : "border-white/10 hover:border-white/30"}`}
                style={{ width: 62, height: 62 }}>
                <img src={src(m)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {post && (
        <aside data-testid="lightbox-panel"
          className="w-full lg:w-[380px] shrink-0 border-t lg:border-t-0 lg:border-l border-white/10 overflow-y-auto max-h-[45vh] lg:max-h-none p-4"
          style={{ background: "rgba(10,10,14,0.92)" }}>
          <div className="flex items-center gap-2.5">
            <Avatar person={post.author} size={36} />
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{post.author?.name}</div>
              <div className="text-[11px] text-white/40">{post.created_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
          </div>

          {post.text && (
            <p className="text-sm text-white/85 whitespace-pre-wrap mt-3">
              <RichText text={post.text} />
            </p>
          )}

          <div className="flex items-center gap-1 mt-3 flex-wrap" data-testid="lightbox-reactions">
            {reactions.map((r) => (
              <button key={r.key} title={r.label} data-testid={`lightbox-react-${r.key}`}
                onClick={() => onReact?.(r.key)}
                className={`text-xl px-1.5 py-1 rounded-lg transition-transform hover:scale-125 ${
                  post.my_reaction === r.key ? "bg-white/10" : ""}`}>
                {r.emoji}
              </button>
            ))}
            {post.total_reactions > 0 && (
              <span className="text-[11px] text-white/50 ml-1" data-testid="lightbox-reaction-count">
                {post.total_reactions}
              </span>
            )}
          </div>

          {commentsOff ? (
            <div className="text-xs text-white/50 mt-4" data-testid="lightbox-comments-off">
              El autor desactivó los comentarios de esta publicación.
            </div>
          ) : (
            <Comments postId={post.post_id} me={me} onCountChange={onCommentAdded} />
          )}
        </aside>
      )}
    </div>
  ), document.body);
};

export default MediaLightbox;
